/** The order's Activity tab (plan §1.1, §4.4): the order's change history — one
 *  `HistoryPanel`, a save's rows grouped by its correlation (an order-ref change that
 *  re-ref'd the linked bookings shows their rows on each booking's own page) — and the
 *  grids where the rest of its trail lives. The history needs console_audit_view (the
 *  changes API's permission). */
import { Box, Link, Stack, Typography } from '@mui/material';

import type { OrderDetail } from '@/api/orders';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { SectionCard } from '@/components/ReadView';
import { usePermissions } from '@/hooks/usePermissions';
import { bookingsHref, cond, deliveryLogsHref, importRunsHref } from '@/utils/gridLinks';

/** The objchange content type of the record an order save writes. */
export const ORDER_CONTENT_TYPE = 'demo.order';

export function OrderActivityTab({ orderId, detail }: { orderId: number; detail: OrderDetail }) {
  const { hasPerm } = usePermissions();
  const canViewHistory = hasPerm('console_audit_view');
  const tenantId = detail.tenant?.id ?? null;
  const ref = detail.order_ref;
  const elsewhere = ref
    ? [
        { label: 'Delivery logs for this order', href: deliveryLogsHref([cond('order', ref)], { tenant: tenantId }) },
        { label: 'Import runs for this order', href: importRunsHref([cond('order', ref, 'exact')], tenantId) },
        { label: 'Bookings in this order', href: bookingsHref([cond('order', ref)], tenantId) },
      ]
    : [];

  return (
    <Box>
      <SectionCard title="Change history">
        {canViewHistory ? (
          <HistoryPanel contentType={ORDER_CONTENT_TYPE} objectPk={String(orderId)} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Change history needs the console_audit_view permission.
          </Typography>
        )}
      </SectionCard>
      {elsewhere.length > 0 && (
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ typography: 'body2' }} data-activity-links>
          {elsewhere.map((l) => (
            <Link key={l.label} href={l.href} target="_blank" rel="noopener noreferrer" underline="hover">
              {l.label}
            </Link>
          ))}
        </Stack>
      )}
    </Box>
  );
}
